/**
 * Δημόσιος QR τιμοκατάλογος — /menu/:token (Ιούν 2026)
 *
 * Mobile-first, read-only. Κανένα auth, κανένα DeviceGate.
 * Paged εμφάνιση: μία κατηγορία ανά «σελίδα» με slide animation,
 * εσωτερικό scroll container (το global body έχει overflow hidden)
 * και πλωτά βελάκια πάνω/κάτω κάτω-δεξιά.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { loadQrMenuFonts, lineStyle } from "@/lib/qrmenuFonts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPrice = (v) => `${Number(v || 0).toFixed(2)} €`;

export default function PublicMenu() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState(0);
  const [slideKey, setSlideKey] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    loadQrMenuFonts();
    axios
      .get(`${API}/public/qrmenu/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Ο τιμοκατάλογος δεν είναι διαθέσιμος"));
  }, [token]);

  const changeCat = (i) => {
    if (!data) return;
    const n = data.categories.length;
    const next = ((i % n) + n) % n;
    setActiveCat(next);
    setSlideKey((k) => k + 1);
    listRef.current?.scrollTo({ top: 0 });
  };

  const scrollList = (dir) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * Math.round(el.clientHeight * 0.7), behavior: "smooth" });
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-zinc-950 text-zinc-300 grid place-items-center px-8 text-center" data-testid="qrmenu-error">
        <div>
          <div className="text-5xl mb-4">☕</div>
          <div className="font-bold text-lg">{error}</div>
          <div className="text-sm text-zinc-500 mt-2">Ρωτήστε το προσωπικό για τον ενημερωμένο τιμοκατάλογο.</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="fixed inset-0 bg-zinc-950 text-zinc-500 grid place-items-center" data-testid="qrmenu-loading">
        <div className="animate-pulse text-sm tracking-widest uppercase">Φόρτωση τιμοκαταλόγου…</div>
      </div>
    );
  }

  const cat = data.categories[activeCat];

  return (
    <div className="fixed inset-0 bg-zinc-950 text-zinc-100 flex flex-col" data-testid="qrmenu-page">
      <style>{`
        @keyframes qrmenu-slide { from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: translateX(0); } }
        .qrmenu-slide { animation: qrmenu-slide 0.28s ease-out; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>

      {/* Header */}
      <header className="pt-6 pb-4 px-6 text-center shrink-0">
        {data.logo && (
          <img
            src={data.logo}
            alt="logo"
            className="w-20 h-20 mx-auto mb-2 rounded-full object-cover border-2 border-amber-500/40 bg-white/5"
            data-testid="qrmenu-logo"
          />
        )}
        <div className="text-[10px] tracking-[0.35em] uppercase text-amber-500/80 mb-1.5">Τιμοκαταλογος</div>
        {data.header?.line1?.text || data.header?.line2?.text ? (
          <div data-testid="qrmenu-business-name">
            {data.header.line1.text && (
              <h1 className="text-2xl text-amber-50 leading-tight" style={lineStyle(data.header.line1)} data-testid="qrmenu-header-line1">
                {data.header.line1.text}
              </h1>
            )}
            {data.header.line2.text && (
              <div className="text-base text-amber-100/90 mt-0.5" style={lineStyle(data.header.line2)} data-testid="qrmenu-header-line2">
                {data.header.line2.text}
              </div>
            )}
          </div>
        ) : (
          <h1 className="font-heading text-2xl font-bold text-amber-50" data-testid="qrmenu-business-name">
            {data.business_name}
          </h1>
        )}
      </header>

      {/* Category chips */}
      {data.categories.length > 0 && (
        <nav className="shrink-0 bg-zinc-950/90 border-y border-zinc-900 px-3 py-2.5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="qrmenu-cat-nav">
          {data.categories.map((c, i) => (
            <button
              key={i}
              onClick={() => changeCat(i)}
              data-testid={`qrmenu-cat-chip-${i}`}
              className={`shrink-0 px-4 h-9 rounded-full text-sm font-bold transition-colors ${
                activeCat === i
                  ? "bg-amber-500 text-zinc-950"
                  : "bg-zinc-900 text-zinc-300 border border-zinc-800"
              }`}
            >
              {c.name}
            </button>
          ))}
        </nav>
      )}

      {/* Ενεργή κατηγορία — μία «σελίδα» με εσωτερικό scroll */}
      <main ref={listRef} className="flex-1 overflow-y-auto overscroll-contain px-5 pb-28" style={{ WebkitOverflowScrolling: "touch" }} data-testid="qrmenu-list">
        {!cat ? (
          <div className="text-center text-zinc-500 py-16" data-testid="qrmenu-empty">
            Ο τιμοκατάλογος ενημερώνεται…
          </div>
        ) : (
          <section key={slideKey} className="qrmenu-slide pt-6 max-w-lg mx-auto" data-testid={`qrmenu-category-${activeCat}`}>
            <div className="flex items-center gap-3 mb-4">
              {data.categories.length > 1 && (
                <button onClick={() => changeCat(activeCat - 1)} className="p-1 text-zinc-500" data-testid="qrmenu-cat-prev" aria-label="Προηγούμενη κατηγορία">
                  <ChevronLeft size={20} />
                </button>
              )}
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.color || "#f59e0b" }} />
              <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-amber-100 flex-1 text-center">{cat.name}</h2>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.color || "#f59e0b" }} />
              {data.categories.length > 1 && (
                <button onClick={() => changeCat(activeCat + 1)} className="p-1 text-zinc-500" data-testid="qrmenu-cat-next" aria-label="Επόμενη κατηγορία">
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
            <ul className="space-y-4">
              {cat.items.map((it, j) => (
                <li key={j} className="flex items-baseline gap-2" data-testid={`qrmenu-item-${activeCat}-${j}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[15px] leading-snug">{it.name}</div>
                    {it.comment && (
                      <div className="text-[12px] text-zinc-500 leading-snug mt-0.5">{it.comment}</div>
                    )}
                    {(it.extras || []).map((ex, k) => (
                      <div key={k} className="text-[12px] leading-snug mt-1" data-testid={`qrmenu-item-extras-${activeCat}-${j}-${k}`}>
                        <span className="font-semibold text-amber-200/80">{ex.name}: </span>
                        <span className="text-zinc-400">
                          {ex.options
                            .map((o) => o.name + (o.price_delta ? ` (+${Number(o.price_delta).toFixed(2)}€)` : ""))
                            .join(" · ")}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex-1 border-b border-dotted border-zinc-700 translate-y-[-3px] min-w-[16px]" />
                  <div className="font-bold text-amber-400 whitespace-nowrap text-[15px]">{fmtPrice(it.price)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-10 text-center text-[10px] text-zinc-700 tracking-widest uppercase">
              Ponte POS · QR Menu
            </div>
          </section>
        )}
      </main>

      {/* Πλωτά βελάκια scroll — κάτω δεξιά */}
      <div className="fixed bottom-5 right-4 flex flex-col gap-2 z-20">
        <button
          onClick={() => scrollList(-1)}
          className="w-11 h-11 rounded-full bg-zinc-800/90 border border-zinc-700 text-amber-400 grid place-items-center active:scale-95 transition-transform shadow-lg"
          data-testid="qrmenu-scroll-up"
          aria-label="Πάνω"
        >
          <ChevronUp size={22} />
        </button>
        <button
          onClick={() => scrollList(1)}
          className="w-11 h-11 rounded-full bg-zinc-800/90 border border-zinc-700 text-amber-400 grid place-items-center active:scale-95 transition-transform shadow-lg"
          data-testid="qrmenu-scroll-down"
          aria-label="Κάτω"
        >
          <ChevronDown size={22} />
        </button>
      </div>
    </div>
  );
}
